import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ArrowLeft, Save, Plus, Trash2, Loader2, Sliders, Package, BarChart3, Users, Plug, CheckCircle2, XCircle, Activity, Gift, Zap, RefreshCw, Bell, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { id: "strategy", label: "Strategia", icon: Sliders },
  { id: "packages", label: "Pacchetti", icon: Package },
  { id: "plans", label: "Abbonamenti", icon: CalendarClock },
  { id: "assets", label: "Asset & Payout", icon: BarChart3 },
  { id: "users", label: "Utenti", icon: Users },
  { id: "pocket", label: "Pocket Option", icon: Plug },
  { id: "services", label: "Servizi", icon: Activity },
];

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("strategy");

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-[#00FFC2] border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#0A0D14] text-white">
      <header className="fixed top-0 inset-x-0 z-50 h-16 border-b border-[#243047] bg-[#0A0D14]/80 backdrop-blur-md">
        <div className="max-w-6xl mx-auto h-full px-4 md:px-6 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white" data-testid="admin-back-btn">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <span className="font-heading font-bold">Pannello Admin</span>
          <span className="w-20" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 pt-20 pb-12">
        <div className="flex gap-2 overflow-auto pb-2 mb-6">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition border ${
                tab === t.id ? "bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] border-[#00FFC2]" : "border-[#243047] text-[#94A3B8] hover:text-white"
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "strategy" && <StrategyTab />}
        {tab === "packages" && <PackagesTab />}
        {tab === "plans" && <PlansTab />}
        {tab === "assets" && <AssetsTab />}
        {tab === "users" && <UsersTab />}
        {tab === "pocket" && <PocketTab />}
        {tab === "services" && <ServicesTab />}
      </main>
    </div>
  );
}

function Card({ children }) {
  return <div className="rounded-xl border border-[#243047] bg-[#121824] p-6">{children}</div>;
}
const inputCls = "w-full bg-[#0A0D14] border border-[#243047] rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-[#00FFC2]";

function StrategyTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)).catch(() => toast.error("Errore caricamento")); }, []);
  if (!s) return <Loader2 className="w-6 h-6 animate-spin text-[#00FFC2]" />;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        min_candles: Number(s.min_candles),
        max_candles: Number(s.max_candles ?? 10),
        min_payout: Number(s.min_payout ?? 70),
        free_signals: Number(s.free_signals),
        martingale_multipliers: s.martingale_multipliers.map(Number),
        base_stake: Number(s.base_stake),
        enabled_timeframes: (s.enabled_timeframes && s.enabled_timeframes.length ? s.enabled_timeframes : [2, 3, 5, 10, 15]).map(Number),
      });
      toast.success("Strategia salvata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    finally { setSaving(false); }
  };

  const ALL_TFS = [2, 3, 5, 10, 15];
  const tfs = s.enabled_timeframes || ALL_TFS;
  const toggleTf = (t) => {
    const set = new Set(tfs);
    set.has(t) ? set.delete(t) : set.add(t);
    setS({ ...s, enabled_timeframes: ALL_TFS.filter((x) => set.has(x)) });
  };

  return (
    <Card>
      <h3 className="font-heading font-bold text-lg mb-5">Configurazione strategia</h3>
      <div className="space-y-6 max-w-lg">
        <div>
          <label className="text-sm font-semibold text-[#94A3B8]">Candele consecutive: parte da <span className="text-white font-mono">{s.max_candles ?? 10}</span> e scende fino a <span className="text-white font-mono">{s.min_candles}</span></label>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <div>
              <div className="text-[11px] text-[#64748B] mb-1">Massimo (inizio ricerca)</div>
              <input type="range" min={5} max={20} value={s.max_candles ?? 10} data-testid="admin-strategy-max-candles-input"
                onChange={(e) => setS({ ...s, max_candles: Number(e.target.value) })} className="w-full accent-[#EC1E9C]" />
            </div>
            <div>
              <div className="text-[11px] text-[#64748B] mb-1">Minimo (fine ricerca)</div>
              <input type="range" min={5} max={20} value={s.min_candles} data-testid="admin-strategy-min-candles-input"
                onChange={(e) => setS({ ...s, min_candles: Number(e.target.value) })} className="w-full accent-[#00FFC2]" />
            </div>
          </div>
          <p className="text-[11px] text-[#64748B] mt-1.5">Ordine di ricerca: numero di candele (dal massimo al minimo) → timeframe più lungo → payout più alto. Forex e OTC alla pari.</p>
        </div>
        <div>
          <label className="text-sm font-semibold text-[#94A3B8]">Payout minimo per inviare un segnale (%)</label>
          <input type="number" min={0} max={100} value={s.min_payout ?? 70} data-testid="admin-strategy-min-payout-input"
            onChange={(e) => setS({ ...s, min_payout: e.target.value })} className={`${inputCls} mt-1.5`} />
          <p className="text-[11px] text-[#64748B] mt-1.5">I mercati con payout inferiore vengono ignorati dalla ricerca.</p>
        </div>
        <div>
          <label className="text-sm font-semibold text-[#94A3B8]">Timeframe da analizzare</label>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="admin-timeframes">
            {ALL_TFS.map((t) => {
              const on = tfs.includes(t);
              return (
                <button key={t} type="button" onClick={() => toggleTf(t)} data-testid={`admin-tf-${t}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-mono font-bold border transition ${
                    on ? "bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] border-[#00FFC2]" : "border-[#243047] text-[#94A3B8] hover:text-white"
                  }`}>
                  {t}m
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-[#64748B] mt-1.5">Il bot cercherà i segnali solo sui timeframe selezionati.</p>
        </div>
        <div>
          <label className="text-sm font-semibold text-[#94A3B8]">Segnali gratuiti alla registrazione</label>
          <input type="number" min={0} value={s.free_signals} data-testid="admin-free-signals-input"
            onChange={(e) => setS({ ...s, free_signals: e.target.value })} className={`${inputCls} mt-1.5`} />
        </div>
        <div>
          <label className="text-sm font-semibold text-[#94A3B8]">Moltiplicatori Martingala (3 step)</label>
          <div className="grid grid-cols-3 gap-2 mt-1.5">
            {s.martingale_multipliers.map((m, i) => (
              <input key={i} type="number" step="0.1" value={m} data-testid={`admin-martingale-${i}`}
                onChange={(e) => { const arr = [...s.martingale_multipliers]; arr[i] = e.target.value; setS({ ...s, martingale_multipliers: arr }); }}
                className={inputCls} />
            ))}
          </div>
        </div>
        <div>
          <label className="text-sm font-semibold text-[#94A3B8]">Puntata base</label>
          <input type="number" step="0.1" value={s.base_stake} data-testid="admin-base-stake-input"
            onChange={(e) => setS({ ...s, base_stake: e.target.value })} className={`${inputCls} mt-1.5`} />
        </div>
        <button onClick={save} disabled={saving} data-testid="admin-save-strategy-button"
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] font-bold hover:brightness-110 transition disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva
        </button>
      </div>
    </Card>
  );
}

function PackagesTab() {
  const [pkgs, setPkgs] = useState([]);
  const load = () => api.get("/admin/packages").then(({ data }) => setPkgs(data)).catch(() => toast.error("Errore caricamento"));
  useEffect(() => { load(); }, []);

  const update = (id, patch) => setPkgs((p) => p.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const save = async (p) => {
    try {
      await api.put(`/admin/packages/${p.id}`, {
        name: p.name, signals: Number(p.signals), price: Number(p.price),
        active: p.active, promo: p.promo, promo_label: p.promo_label || "",
      });
      toast.success("Pacchetto salvato");
    } catch { toast.error("Errore"); }
  };
  const add = async () => {
    const { data } = await api.post("/admin/packages", { name: "Nuovo", signals: 1, price: 1, active: true, promo: false, promo_label: "" });
    setPkgs((p) => [...p, data]);
  };
  const del = async (id) => { await api.delete(`/admin/packages/${id}`); setPkgs((p) => p.filter((x) => x.id !== id)); };

  return (
    <Card>
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-heading font-bold text-lg">Pacchetti & Promo</h3>
        <button onClick={add} data-testid="admin-add-package" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#243047] text-sm hover:text-[#00FFC2]"><Plus className="w-4 h-4" /> Aggiungi</button>
      </div>
      <div className="space-y-3">
        {pkgs.map((p) => (
          <div key={p.id} className="grid grid-cols-2 md:grid-cols-7 gap-2 items-center rounded-lg bg-[#0A0D14] border border-[#243047] p-3" data-testid="admin-package-row">
            <input value={p.name} onChange={(e) => update(p.id, { name: e.target.value })} className={inputCls} placeholder="Nome" />
            <input type="number" value={p.signals} onChange={(e) => update(p.id, { signals: e.target.value })} className={inputCls} placeholder="Segnali" />
            <input type="number" step="0.5" value={p.price} data-testid="admin-package-price-input" onChange={(e) => update(p.id, { price: e.target.value })} className={inputCls} placeholder="Prezzo $" />
            <input value={p.promo_label || ""} onChange={(e) => update(p.id, { promo_label: e.target.value })} className={inputCls} placeholder="Etichetta promo" />
            <label className="flex items-center gap-1.5 text-xs text-[#94A3B8]"><input type="checkbox" checked={p.promo} onChange={(e) => update(p.id, { promo: e.target.checked })} className="accent-[#EC1E9C]" /> Promo</label>
            <label className="flex items-center gap-1.5 text-xs text-[#94A3B8]"><input type="checkbox" checked={p.active} onChange={(e) => update(p.id, { active: e.target.checked })} className="accent-[#00FFC2]" /> Attivo</label>
            <div className="flex gap-1.5">
              <button onClick={() => save(p)} className="flex-1 rounded-md bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] text-xs font-bold py-2 hover:brightness-110" data-testid="admin-save-package"><Save className="w-3.5 h-3.5 inline" /></button>
              <button onClick={() => del(p.id)} className="rounded-md bg-[#EC1E9C]/20 text-[#EC1E9C] px-2 hover:bg-[#EC1E9C]/30"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PlansTab() {
  const [plans, setPlans] = useState([]);
  const load = () => api.get("/admin/plans").then(({ data }) => setPlans(data)).catch(() => toast.error("Errore caricamento"));
  useEffect(() => { load(); }, []);

  const update = (id, patch) => setPlans((p) => p.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const save = async (p) => {
    try {
      await api.put(`/admin/plans/${p.id}`, {
        name: p.name, daily_signals: Number(p.daily_signals), price: Number(p.price),
        active: p.active, promo: p.promo, promo_label: p.promo_label || "",
      });
      toast.success("Abbonamento salvato");
    } catch { toast.error("Errore"); }
  };
  const add = async () => {
    const { data } = await api.post("/admin/plans", { name: "Nuovo piano", daily_signals: 1, price: 9.99, active: true, promo: false, promo_label: "" });
    setPlans((p) => [...p, data]);
  };
  const del = async (id) => { await api.delete(`/admin/plans/${id}`); setPlans((p) => p.filter((x) => x.id !== id)); };

  return (
    <Card>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading font-bold text-lg">Abbonamenti settimanali</h3>
        <button onClick={add} data-testid="admin-add-plan" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#243047] text-sm hover:text-[#00FFC2]"><Plus className="w-4 h-4" /> Aggiungi</button>
      </div>
      <p className="text-sm text-[#94A3B8] mb-5">
        Prezzo <span className="text-white font-semibold">a settimana</span> con rinnovo automatico Stripe. I crediti giornalieri vengono ricaricati alle <span className="text-white font-semibold">00:00 ora italiana</span> e, se non usati, si azzerano; si sommano ai crediti dei pacchetti.
      </p>
      <div className="space-y-3">
        {plans.map((p) => (
          <div key={p.id} className="grid grid-cols-2 md:grid-cols-7 gap-2 items-center rounded-lg bg-[#0A0D14] border border-[#243047] p-3" data-testid="admin-plan-row">
            <input value={p.name} onChange={(e) => update(p.id, { name: e.target.value })} className={inputCls} placeholder="Nome" data-testid="admin-plan-name-input" />
            <div className="flex items-center gap-1"><input type="number" min={1} value={p.daily_signals} onChange={(e) => update(p.id, { daily_signals: e.target.value })} className={inputCls} data-testid="admin-plan-daily-input" /><span className="text-[10px] text-[#64748B] whitespace-nowrap">/giorno</span></div>
            <div className="flex items-center gap-1"><input type="number" step="0.01" value={p.price} onChange={(e) => update(p.id, { price: e.target.value })} className={inputCls} data-testid="admin-plan-price-input" /><span className="text-[10px] text-[#64748B] whitespace-nowrap">$/sett.</span></div>
            <input value={p.promo_label || ""} onChange={(e) => update(p.id, { promo_label: e.target.value })} className={inputCls} placeholder="Etichetta promo" />
            <label className="flex items-center gap-1.5 text-xs text-[#94A3B8]"><input type="checkbox" checked={p.promo} onChange={(e) => update(p.id, { promo: e.target.checked })} className="accent-[#EC1E9C]" /> Promo</label>
            <label className="flex items-center gap-1.5 text-xs text-[#94A3B8]"><input type="checkbox" checked={p.active} onChange={(e) => update(p.id, { active: e.target.checked })} className="accent-[#00FFC2]" /> Attivo</label>
            <div className="flex gap-1.5">
              <button onClick={() => save(p)} className="flex-1 rounded-md bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] text-xs font-bold py-2 hover:brightness-110" data-testid="admin-save-plan"><Save className="w-3.5 h-3.5 inline" /></button>
              <button onClick={() => del(p.id)} className="rounded-md bg-[#EC1E9C]/20 text-[#EC1E9C] px-2 hover:bg-[#EC1E9C]/30" data-testid="admin-delete-plan"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

const CATEGORY_LABELS = { forex: "Forex", currency: "Valute", stock: "Azioni", cryptocurrency: "Crypto", commodity: "Materie prime", index: "Indici" };

function AssetsTab() {
  const [assets, setAssets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [syncing, setSyncing] = useState(false);
  const load = () => api.get("/admin/assets").then(({ data }) => setAssets(data)).catch(() => toast.error("Errore caricamento"));
  useEffect(() => { load(); }, []);
  const update = (id, patch) => setAssets((a) => a.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const save = async (a) => {
    try {
      await api.put(`/admin/assets/${a.id}`, { symbol: a.symbol, display_name: a.display_name, payout: Number(a.payout), data_source: a.data_source, category: a.category || "currency", active: a.active });
      toast.success("Asset salvato");
    } catch { toast.error("Errore"); }
  };
  const add = async () => {
    const { data } = await api.post("/admin/assets", { symbol: "NEW_otc", display_name: "Nuovo OTC", payout: 85, data_source: "pocketoption", category: "currency", active: true });
    setAssets((a) => [...a, data]);
  };
  const del = async (id) => { await api.delete(`/admin/assets/${id}`); setAssets((a) => a.filter((x) => x.id !== id)); };
  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/assets/sync-otc");
      toast.success(`Sincronizzati ${data.total_otc} mercati OTC (${data.added} nuovi, ${data.updated} aggiornati)`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Sincronizzazione fallita"); }
    finally { setSyncing(false); }
  };
  const toggleCategory = async (cat, active) => {
    const targets = assets.filter((a) => (a.category || "currency") === cat && a.data_source === "pocketoption");
    await Promise.all(targets.map((a) => api.put(`/admin/assets/${a.id}`, { symbol: a.symbol, display_name: a.display_name, payout: Number(a.payout), data_source: a.data_source, category: a.category || "currency", active })));
    toast.success(`${CATEGORY_LABELS[cat] || cat}: ${active ? "attivati" : "disattivati"} ${targets.length} mercati`);
    load();
  };

  const groups = ["all", "forex", "currency", "stock", "cryptocurrency", "commodity"];
  const visible = assets.filter((a) => filter === "all" ? true : filter === "forex" ? a.data_source === "forex" : (a.data_source === "pocketoption" && (a.category || "currency") === filter));
  const count = (g) => g === "all" ? assets.length : g === "forex" ? assets.filter((a) => a.data_source === "forex").length : assets.filter((a) => a.data_source === "pocketoption" && (a.category || "currency") === g).length;

  return (
    <Card>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h3 className="font-heading font-bold text-lg">Asset & Payout <span className="text-xs font-normal text-[#64748B]">({assets.length} mercati)</span></h3>
        <div className="flex gap-2">
          <button onClick={sync} disabled={syncing} data-testid="admin-sync-otc" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] text-sm font-bold hover:brightness-110 disabled:opacity-60">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sincronizza OTC da Pocket Option
          </button>
          <button onClick={add} data-testid="admin-add-asset" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#243047] text-sm hover:text-[#00FFC2]"><Plus className="w-4 h-4" /> Aggiungi</button>
        </div>
      </div>
      <p className="text-sm text-[#94A3B8] mb-4">
        I mercati OTC (valute, azioni, crypto, materie prime) e i loro payout vengono sincronizzati automaticamente da Pocket Option ogni 5 minuti. Spunta <span className="text-white font-semibold">"In ricerca"</span> per i mercati da analizzare; il payout minimo si imposta in Strategia.
      </p>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-asset-filters">
        {groups.map((g) => (
          <button key={g} onClick={() => setFilter(g)} data-testid={`admin-asset-filter-${g}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${filter === g ? "border-[#00FFC2] text-[#00FFC2] bg-[#00FFC2]/10" : "border-[#243047] text-[#94A3B8] hover:text-white"}`}>
            {g === "all" ? "Tutti" : g === "forex" ? "Forex" : `${CATEGORY_LABELS[g]} OTC`} <span className="font-mono opacity-70">({count(g)})</span>
          </button>
        ))}
        {filter !== "all" && filter !== "forex" && (
          <div className="ml-auto flex gap-1.5">
            <button onClick={() => toggleCategory(filter, true)} className="px-2.5 py-1.5 rounded-lg text-xs border border-[#243047] text-[#00FFC2] hover:bg-[#00FFC2]/10" data-testid="admin-asset-enable-category">Attiva tutti</button>
            <button onClick={() => toggleCategory(filter, false)} className="px-2.5 py-1.5 rounded-lg text-xs border border-[#243047] text-[#EC1E9C] hover:bg-[#EC1E9C]/10" data-testid="admin-asset-disable-category">Disattiva tutti</button>
          </div>
        )}
      </div>
      <div className="space-y-2 max-h-[70vh] overflow-auto pr-1">
        {visible.map((a) => (
          <div key={a.id} className="grid grid-cols-2 md:grid-cols-7 gap-2 items-center rounded-lg bg-[#0A0D14] border border-[#243047] p-3" data-testid="admin-asset-payout-row">
            <input value={a.display_name} onChange={(e) => update(a.id, { display_name: e.target.value })} className={inputCls} placeholder="Nome" />
            <input value={a.symbol} onChange={(e) => update(a.id, { symbol: e.target.value })} className={`${inputCls} font-mono`} placeholder="Simbolo" />
            <select value={a.data_source} onChange={(e) => update(a.id, { data_source: e.target.value })} className={inputCls}>
              <option value="forex">Forex (live)</option>
              <option value="pocketoption">OTC (Pocket Option)</option>
            </select>
            <div className="flex items-center gap-1.5">
              <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${a.data_source === "forex" ? "border-[#00FFC2]/40 text-[#00FFC2]" : "border-[#EC1E9C]/40 text-[#EC1E9C]"}`}>{a.data_source === "forex" ? "Forex" : CATEGORY_LABELS[a.category] || a.category || "OTC"}</span>
              {a.po_open === false && <span className="text-[10px] text-[#64748B]">chiuso</span>}
            </div>
            <div className="flex items-center gap-1"><input type="number" value={a.payout} onChange={(e) => update(a.id, { payout: e.target.value })} className={inputCls} placeholder="Payout %" /><span className="text-xs text-[#64748B]">%</span></div>
            <label className="flex items-center gap-1.5 text-xs text-[#94A3B8]"><input type="checkbox" checked={a.active} onChange={(e) => update(a.id, { active: e.target.checked })} className="accent-[#00FFC2]" data-testid={`admin-asset-search-${a.symbol}`} /> In ricerca</label>
            <div className="flex gap-1.5">
              <button onClick={() => save(a)} className="flex-1 rounded-md bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] text-xs font-bold py-2 hover:brightness-110" data-testid="admin-save-asset"><Save className="w-3.5 h-3.5 inline" /></button>
              <button onClick={() => del(a.id)} className="rounded-md bg-[#EC1E9C]/20 text-[#EC1E9C] px-2 hover:bg-[#EC1E9C]/30"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [bulk, setBulk] = useState(2);
  const [target, setTarget] = useState("non_admin");
  const [gifting, setGifting] = useState(false);
  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => toast.error("Errore caricamento"));
  useEffect(() => { load(); api.get("/admin/plans").then(({ data }) => setPlans(data)).catch(() => {}); }, []);
  const setSubscription = async (u, planId) => {
    try {
      await api.put(`/admin/users/${u.id}/subscription`, { plan_id: planId || null });
      toast.success(planId ? "Abbonamento assegnato (7 giorni)" : "Abbonamento rimosso");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };
  const setCredits = async (u, credits) => {
    try { await api.put(`/admin/users/${u.id}/credits`, { credits: Number(credits) }); toast.success("Crediti aggiornati"); }
    catch { toast.error("Errore"); }
  };
  const giftOne = async (u) => {
    try { await api.post(`/admin/users/${u.id}/gift-credits`, { amount: Number(bulk) || 1 }); toast.success(`+${bulk} crediti a ${u.email}`); load(); }
    catch { toast.error("Errore"); }
  };
  const giftAll = async () => {
    setGifting(true);
    try { const { data } = await api.post(`/admin/users/gift-credits`, { amount: Number(bulk) || 1, target }); toast.success(`Regalati ${bulk} crediti a ${data.updated} utenti`); load(); }
    catch { toast.error("Errore"); } finally { setGifting(false); }
  };
  const toggleBan = async (u) => {
    try { await api.put(`/admin/users/${u.id}/ban`, { banned: !u.banned }); toast.success(u.banned ? "Utente sbloccato" : "Utente bannato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Eliminare definitivamente ${u.email}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Utente eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };
  return (
    <Card>
      <h3 className="font-heading font-bold text-lg mb-4">Utenti</h3>

      <div className="rounded-lg bg-[#0A0D14] border border-[#243047] p-4 mb-5">
        <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-white"><Gift className="w-4 h-4 text-[#00FFC2]" /> Regala segnali gratuiti</div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[11px] text-[#94A3B8] uppercase">Crediti</label>
            <input type="number" min={1} value={bulk} onChange={(e) => setBulk(e.target.value)} className={`${inputCls} w-24`} data-testid="admin-gift-amount" />
          </div>
          <div>
            <label className="text-[11px] text-[#94A3B8] uppercase">Destinatari</label>
            <select value={target} onChange={(e) => setTarget(e.target.value)} className={`${inputCls} w-48`} data-testid="admin-gift-target">
              <option value="non_admin">Tutti gli utenti registrati</option>
              <option value="all">Tutti (inclusi admin)</option>
            </select>
          </div>
          <button onClick={giftAll} disabled={gifting} data-testid="admin-gift-all"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] font-bold hover:brightness-110 transition disabled:opacity-50">
            {gifting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />} Regala a tutti
          </button>
        </div>
      </div>

      <div className="overflow-auto" data-testid="admin-user-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[#64748B] text-xs uppercase"><th className="py-2">Email</th><th>Ruolo</th><th>Crediti</th><th>Regala</th><th>Abbonamento</th><th>Stato</th><th>Azioni</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#243047]">
                <td className="py-2.5 text-white">{u.email}</td>
                <td><span className={`text-xs font-mono ${u.role === "admin" ? "text-[#EC1E9C]" : "text-[#94A3B8]"}`}>{u.role}</span></td>
                <td>
                  <input type="number" defaultValue={u.credits} onBlur={(e) => setCredits(u, e.target.value)}
                    className="w-24 bg-[#0A0D14] border border-[#243047] rounded px-2 py-1 text-white font-mono" data-testid={`admin-user-credits-${u.email}`} />
                </td>
                <td>
                  <button onClick={() => giftOne(u)} className="flex items-center gap-1 text-xs font-bold text-[#00FFC2] hover:brightness-110" data-testid={`admin-gift-user-${u.email}`}>
                    <Gift className="w-3.5 h-3.5" /> +{bulk}
                  </button>
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <select value={u.subscription?.plan_id || ""} onChange={(e) => setSubscription(u, e.target.value)}
                      className="bg-[#0A0D14] border border-[#243047] rounded px-2 py-1 text-xs text-white" data-testid={`admin-user-subscription-${u.email}`}>
                      <option value="">Nessuno</option>
                      {plans.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.daily_signals}/g)</option>)}
                    </select>
                    {u.subscription && <span className="text-[10px] font-mono text-[#00FFC2]" title="Crediti abbonamento oggi">{u.sub_credits}/{u.subscription.daily_signals}</span>}
                  </div>
                </td>
                <td>
                  {u.banned
                    ? <span className="text-[11px] font-mono text-[#EC1E9C]">bannato</span>
                    : <span className="text-[11px] font-mono text-[#00FFC2]">attivo</span>}
                </td>
                <td>
                  {u.role !== "admin" && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleBan(u)} data-testid={`admin-ban-${u.email}`}
                        className={`text-xs font-semibold ${u.banned ? "text-[#00FFC2]" : "text-[#EC1E9C]"} hover:brightness-110`}>
                        {u.banned ? "Sblocca" : "Banna"}
                      </button>
                      <button onClick={() => removeUser(u)} data-testid={`admin-delete-user-${u.email}`}
                        className="text-[#EC1E9C]/80 hover:text-[#EC1E9C]"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function PocketTab() {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ label: "", email: "", password: "", ssid: "", uid: 0, is_demo: false });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [results, setResults] = useState({});
  const [autoRunning, setAutoRunning] = useState(false);
  const [notifs, setNotifs] = useState([]);

  const load = () => api.get("/admin/po-accounts").then(({ data }) => setAccounts(data)).catch(() => {});
  const loadNotifs = () => api.get("/admin/notifications").then(({ data }) => setNotifs(data.items || [])).catch(() => {});
  useEffect(() => { load(); loadNotifs(); }, []);

  const autoSelect = async () => {
    setAutoRunning(true);
    try {
      const { data } = await api.post("/admin/po-accounts/auto-select");
      if (data.working) toast.success(`Account funzionante: ${data.working}`);
      else toast.error("Nessun account funzionante trovato");
      load(); loadNotifs();
    } catch { toast.error("Errore"); } finally { setAutoRunning(false); }
  };
  const markRead = async () => { try { await api.post("/admin/notifications/read"); loadNotifs(); } catch {} };

  const add = async () => {
    if (!form.email && !form.ssid) { toast.error("Inserisci almeno email o SSID"); return; }
    setSaving(true);
    try {
      await api.post("/admin/po-accounts", { ...form, uid: Number(form.uid) || 0 });
      toast.success("Account aggiunto");
      setForm({ label: "", email: "", password: "", ssid: "", uid: 0, is_demo: false });
      load();
    } catch { toast.error("Errore"); } finally { setSaving(false); }
  };
  const activate = async (id) => { setBusyId(id); try { await api.post(`/admin/po-accounts/${id}/activate`); toast.success("Account attivato"); load(); } catch { toast.error("Errore"); } finally { setBusyId(null); } };
  const del = async (id) => { try { await api.delete(`/admin/po-accounts/${id}`); load(); } catch { toast.error("Errore"); } };
  const test = async (id) => {
    setBusyId(id);
    try { const { data } = await api.post(`/admin/po-accounts/${id}/test`); setResults((r) => ({ ...r, [id]: data })); load(); }
    catch (e) { setResults((r) => ({ ...r, [id]: { connected: false, error: formatApiError(e.response?.data?.detail) || "Errore" } })); }
    finally { setBusyId(null); }
  };

  return (
    <Card>
      <h3 className="font-heading font-bold text-lg mb-2">Account Pocket Option (OTC)</h3>
      <p className="text-sm text-[#94A3B8] mb-4 leading-relaxed">
        Gestisci più <span className="text-white font-semibold">account dedicati</span>. Il bot prova gli account <span className="text-white font-semibold">uno alla volta</span> finché trova quello che funziona;
        se uno smette di funzionare ti avviso qui. Il collegamento usa il <span className="text-white font-semibold">token SSID</span>:
        accedi a Pocket Option dal browser, apri DevTools → Network → WS e copia il messaggio che inizia con <code className="text-[#00E0B0]">42["auth",...]</code>.
        <br /><span className="text-[#EC1E9C]">Nota:</span> il login automatico con sola email/password non è possibile (captcha/2FA); email e password servono come tua anagrafica dell'account. Operazione a rischio ban.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button onClick={autoSelect} disabled={autoRunning} data-testid="po-auto-select"
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] font-bold hover:brightness-110 transition disabled:opacity-50">
          {autoRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Prova automaticamente
        </button>
        <span className="text-xs text-[#64748B]">Testa tutti gli account in ordine e attiva il primo che si connette.</span>
      </div>

      {notifs.length > 0 && (
        <div className="rounded-lg border border-[#243047] bg-[#0A0D14] p-4 mb-5" data-testid="po-notifications">
          <div className="flex items-center justify-between mb-2">
            <span className="flex items-center gap-2 text-sm font-semibold text-white"><Bell className="w-4 h-4 text-[#00FFC2]" /> Notifiche</span>
            <button onClick={markRead} className="text-xs text-[#94A3B8] hover:text-white">Segna come lette</button>
          </div>
          <div className="space-y-1.5 max-h-40 overflow-auto">
            {notifs.map((n) => (
              <div key={n.id} className={`text-xs font-mono flex items-center gap-2 ${n.read ? "text-[#64748B]" : "text-white"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${n.level === "error" ? "bg-[#EC1E9C]" : n.level === "warning" ? "bg-[#00E0B0]" : "bg-[#00FFC2]"}`} />
                {n.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* nuovo account */}
      <div className="rounded-lg bg-[#0A0D14] border border-[#243047] p-4 mb-6">
        <div className="grid md:grid-cols-2 gap-3">
          <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className={inputCls} placeholder="Etichetta (es. Account 1)" data-testid="po-form-label" />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="Email account Pocket Option" data-testid="po-form-email" />
          <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} type="password" className={inputCls} placeholder="Password (promemoria)" data-testid="po-form-password" />
          <input value={form.uid} onChange={(e) => setForm({ ...form, uid: e.target.value })} type="number" className={inputCls} placeholder="UID (opzionale)" data-testid="po-form-uid" />
        </div>
        <textarea value={form.ssid} onChange={(e) => setForm({ ...form, ssid: e.target.value })} rows={2} data-testid="po-form-ssid"
          placeholder='SSID: 42["auth",{"session":"...","isDemo":0,"uid":123,"platform":1}]'
          className={`${inputCls} font-mono text-xs mt-3`} />
        <div className="flex items-center justify-between mt-3">
          <label className="flex items-center gap-2 text-xs text-[#94A3B8]"><input type="checkbox" checked={form.is_demo} onChange={(e) => setForm({ ...form, is_demo: e.target.checked })} className="accent-[#00FFC2]" /> Account demo</label>
          <button onClick={add} disabled={saving} data-testid="po-add-account"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] font-bold hover:brightness-110 transition disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Aggiungi account
          </button>
        </div>
      </div>

      {/* lista account */}
      <div className="space-y-3" data-testid="po-accounts-list">
        {accounts.length === 0 && <p className="text-sm text-[#64748B]">Nessun account configurato.</p>}
        {accounts.map((a) => (
          <div key={a.id} className={`rounded-lg border p-4 ${a.active ? "border-[#00FFC2] bg-[#00FFC2]/5" : "border-[#243047] bg-[#0A0D14]"}`} data-testid="po-account-row">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-heading font-bold text-white">{a.label || a.email || "Account"}</span>
                  {a.active && <span className="text-[10px] font-bold uppercase bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] px-2 py-0.5 rounded-full">Attivo</span>}
                  {a.is_demo && <span className="text-[10px] font-mono uppercase text-[#94A3B8] border border-[#243047] rounded px-1.5 py-0.5">Demo</span>}
                </div>
                <div className="text-[11px] font-mono text-[#64748B] mt-1">
                  {a.email || "—"} · SSID {a.ssid_set ? a.ssid_masked : "non impostato"} · stato: <span className={a.status === "connesso" ? "text-[#00FFC2]" : a.status === "errore" ? "text-[#EC1E9C]" : "text-[#64748B]"}>{a.status}</span>
                </div>
              </div>
              <div className="flex gap-2">
                {!a.active && (
                  <button onClick={() => activate(a.id)} disabled={busyId === a.id} data-testid={`po-activate-${a.id}`}
                    className="px-3 py-1.5 rounded-md bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] text-xs font-bold hover:brightness-110 disabled:opacity-50">Attiva</button>
                )}
                <button onClick={() => test(a.id)} disabled={busyId === a.id} data-testid={`po-test-${a.id}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#243047] text-white text-xs hover:border-[#00E0B0] disabled:opacity-50">
                  {busyId === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />} Testa
                </button>
                <button onClick={() => del(a.id)} className="rounded-md bg-[#EC1E9C]/20 text-[#EC1E9C] px-2 hover:bg-[#EC1E9C]/30"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            {results[a.id] && (
              <div className={`mt-3 flex items-start gap-2 rounded-md px-3 py-2 text-xs ${
                results[a.id].connected ? "bg-[#00FFC2]/10 border border-[#00FFC2]/30 text-[#00FFC2]" : "bg-[#EC1E9C]/10 border border-[#EC1E9C]/30 text-[#EC1E9C]"
              }`}>
                {results[a.id].connected ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <XCircle className="w-4 h-4 mt-0.5" />}
                <span>{results[a.id].connected ? "Connesso. I mercati OTC verranno analizzati con questo account." : results[a.id].error}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}


function ServicesTab() {
  const [svc, setSvc] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/admin/services").then(({ data }) => setSvc(data)).catch(() => toast.error("Errore")).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const Row = ({ label, ok, detail }) => (
    <div className="flex items-center justify-between rounded-lg bg-[#0A0D14] border border-[#243047] p-4">
      <div>
        <div className="font-heading font-bold text-white">{label}</div>
        {detail && <div className="text-[11px] font-mono text-[#64748B] mt-0.5">{detail}</div>}
      </div>
      <span className={`flex items-center gap-1.5 text-xs font-bold font-mono px-2.5 py-1 rounded-md border ${
        ok ? "text-[#00FFC2] border-[#00FFC2]/40 bg-[#00FFC2]/10" : "text-[#EC1E9C] border-[#EC1E9C]/40 bg-[#EC1E9C]/10"
      }`}>
        {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
        {ok ? "Attivo" : "Non disponibile"}
      </span>
    </div>
  );

  return (
    <Card>
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-heading font-bold text-lg">Stato servizi (accesso admin)</h3>
        <button onClick={load} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#243047] text-sm hover:text-[#00FFC2]" data-testid="services-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Aggiorna
        </button>
      </div>
      {loading && !svc ? (
        <Loader2 className="w-6 h-6 animate-spin text-[#00FFC2]" />
      ) : (
        <div className="space-y-3" data-testid="services-status">
          <Row label="Forex — dati live" ok={svc?.forex} detail="Dati candlestick in tempo reale per le coppie forex" />
          <Row label="OTC — Pocket Option"
            ok={svc?.otc?.active?.status === "connesso"}
            detail={svc?.otc?.active
              ? `Account attivo: ${svc.otc.active.label} · stato: ${svc.otc.active.status} · ${svc.otc.total_accounts} account totali`
              : `Nessun account attivo · ${svc?.otc?.total_accounts ?? 0} account totali`} />
        </div>
      )}
    </Card>
  );
}
